import React, { useState, useEffect } from 'react';
import { Edit, Plus, AlertTriangle, Check, X, MessageSquare, FileText, Settings } from 'lucide-react';
import Sidebar from '../../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../../component/b2c/navBar/navBar';
import { useMyContext } from '../../../../../context/MyContext';
import { IndianRupee } from "lucide-react";
import toast from 'react-hot-toast';

const WhatsAppRatesManager = () => {
    const token = localStorage.getItem("token");
    const cus_id = localStorage.getItem("cus_id");
    const { isDarkMode, isToggleSidebar } = useMyContext();
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        message_type: '',
        rate: '',
        description: '',
        status: 'Active'
    });
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertConfig, setAlertConfig] = useState({});

    useEffect(() => {
        fetchRates();
    }, []);

    const fetchRates = async () => {
        try {
            setLoading(true);

            const response = await fetch("https://api.pickupxpress.com/admin/whatsapp/rates", {
                method: "GET",
                headers: {
                    cus_id: cus_id,
                    "Content-Type": "application/json",
                    token: token,
                },
            });

            const data = await response.json();

            if (data.status === true) {
                setRates(data?.data);
            } else {
                toast.error(data.message || "Something went wrong");
            }
        } catch (err) {
            toast.error("Failed to fetch rates");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(
                "https://api.pickupxpress.com/admin/whatsapp/setRate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: cus_id,
                        token: token,
                    },
                    body: JSON.stringify({
                        message_type: formData.message_type,
                        rate: formData.rate,
                        description: formData.description,
                        status: 'Active'
                    }),
                }
            );

            const data = await response.json();

            if (data.status === true) {
                if (editingId) {
                    setRates((prev) =>
                        prev.map((rate) =>
                            rate.id === editingId ? { ...formData, id: editingId } : rate
                        )
                    );
                    showCustomAlert("Rate updated successfully!", "success");
                } else {
                    const newRate = { ...formData, id: data?.insertedId || Date.now() };
                    setRates((prev) => [...prev, newRate]);
                    showCustomAlert("Rate created successfully!", "success");
                }
                resetForm();
            } else {
                showCustomAlert(data.message || "Failed to save rate", "error");
            }
        } catch (err) {
            console.error(err);
            showCustomAlert("Something went wrong while saving the rate", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = (rate, newStatus) => {
        setAlertConfig({
            title: `${newStatus === "Active" ? "Activate" : "Deactivate"} Rate`,
            message: `Are you sure you want to ${newStatus === "Active" ? "activate" : "deactivate"} this rate for "${rate.message_type}"?`,
            confirmText: newStatus === "Active" ? "Activate" : "Deactivate",
            type: "confirm",
            onConfirm: () => updateStatus(rate, newStatus),
        });
        setShowAlert(true);
    };

    const updateStatus = async (rate, newStatus) => {
        setLoading(true);

        try {
            const response = await fetch(
                "https://api.pickupxpress.com/admin/whatsapp/setRate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: cus_id,
                        token: token,
                    },
                    body: JSON.stringify({
                        id: rate.id,
                        message_type: rate.message_type,
                        rate: rate.rate,
                        description: rate.description,
                        status: newStatus,
                    }),
                }
            );

            const data = await response.json();

            if (data.status === true) {
                setRates((prev) =>
                    prev.map((r) =>
                        r.id === rate.id ? { ...r, status: newStatus } : r
                    )
                );

                showCustomAlert(
                    `Rate ${newStatus === "Active" ? "activated" : "deactivated"} successfully!`,
                    "success"
                );
            } else {
                showCustomAlert(data.message || "Failed to update status", "error");
            }
        } catch (err) {
            console.error(err);
            showCustomAlert("Something went wrong while updating status", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (rate) => {
        setFormData({
            message_type: rate.message_type,
            rate: rate.rate,
            description: rate.description,
        });
        setEditingId(rate.id);
        setShowForm(true);
    };

    const resetForm = () => {
        setFormData({
            message_type: '',
            rate: '',
            description: '',
            status: 'Active'
        });
        setEditingId(null);
        setShowForm(false);
    };

    const showCustomAlert = (message, type) => {
        setAlertConfig({
            title: type === 'success' ? 'Success' : 'Error',
            message,
            type,
            onConfirm: () => setShowAlert(false)
        });
        setShowAlert(true);
    };

    const CustomAlert = ({ config, onClose }) => {
        if (!config) return null;

        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                <div className="bg-white dark:bg-[#111827] rounded-xl max-w-sm w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700">
                    <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                            {config.type === 'success' && (
                                <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg shrink-0">
                                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                            )}
                            {config.type === 'error' && (
                                <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg shrink-0">
                                    <X className="w-5 h-5 text-red-500 dark:text-red-400" />
                                </div>
                            )}
                            {config.type === 'confirm' && (
                                <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                                </div>
                            )}
                            <h3 className="text-sm font-bold text-gray-800 dark:text-white">{config.title}</h3>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-5 leading-relaxed">{config.message}</p>
                        <div className="flex justify-end gap-2">
                            {config.type === 'confirm' && (
                                <button
                                    onClick={onClose}
                                    className="px-4 py-1.5 text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    if (config.onConfirm) config.onConfirm();
                                    onClose();
                                }}
                                className="px-4 py-1.5 text-[10px] rounded-lg transition-colors font-medium bg-gray-800 dark:bg-white text-white dark:text-gray-800 hover:opacity-80"
                            >
                                {config.confirmText || 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const RateFormModal = () => {
        if (!showForm) return null;

        return (
            <div
                className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                onClick={resetForm}
            >
                <div
                    className="bg-white dark:bg-[#111827] rounded-xl w-full max-w-lg shadow-2xl border border-gray-200 dark:border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-lg">
                                <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </div>
                            <h2 className="text-[11px] font-bold text-gray-800 dark:text-white uppercase tracking-wider">
                                {editingId ? 'Edit Rate' : 'Add New Rate'}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Modal body */}
                    <div className="p-5 space-y-3.5">
                        <div>
                            <label className="flex items-center text-[9px] font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                                <MessageSquare className="w-3.5 h-3.5 mr-1.5 text-gray-500 dark:text-gray-400" />
                                Message Type
                            </label>
                            <input
                                type="text"
                                value={formData.message_type}
                                onChange={(e) => setFormData({ ...formData, message_type: e.target.value })}
                                className={`w-full px-3 py-1.5 text-[11px] border rounded-lg focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-gray-400 dark:focus:border-gray-500 transition-colors outline-none bg-white dark:bg-[#1F2937] border-gray-200 dark:border-gray-700 text-gray-800 dark:text-white`}
                                placeholder="e.g., shipment_ofd"
                                required
                            />
                        </div>

                        <div>
                            <label className="flex items-center text-[9px] font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                                <IndianRupee className="w-3.5 h-3.5 mr-1.5 text-gray-500 dark:text-gray-400" />
                                Rate
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.rate}
                                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                className={`w-full px-3 py-1.5 text-[11px] border rounded-lg focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-gray-400 dark:focus:border-gray-500 transition-colors outline-none bg-white dark:bg-[#1F2937] border-gray-200 dark:border-gray-700 text-gray-800 dark:text-white`}
                                placeholder="e.g., 2.30"
                                required
                            />
                        </div>

                        <div>
                            <label className="flex items-center text-[9px] font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">
                                <FileText className="w-3.5 h-3.5 mr-1.5 text-gray-500 dark:text-gray-400" />
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className={`w-full px-3 py-1.5 text-[11px] border rounded-lg focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-gray-400 dark:focus:border-gray-500 transition-colors outline-none resize-none bg-white dark:bg-[#1F2937] border-gray-200 dark:border-gray-700 text-gray-800 dark:text-white`}
                                rows="3"
                                placeholder="Enter a detailed description..."
                                required
                            />
                        </div>
                    </div>

                    {/* Modal footer */}
                    <div className="flex justify-end gap-2 px-5 pb-4">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-4 py-1.5 text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || !formData.rate || !formData.description || !formData.message_type}
                            className="px-4 py-1.5 text-[10px] bg-gray-800 dark:bg-white text-white dark:text-gray-800 rounded-lg hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                        >
                            {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const StatusSwitch = ({ checked, onChange, disabled = false }) => (
        <label className="inline-flex items-center gap-2 cursor-pointer group">
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className="sr-only peer"
            />
            <div className={`
                relative w-9 h-[20px] rounded-full transition-colors duration-200
                ${checked ? 'bg-blue-800 dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}>
                <div className={`
                    absolute top-0.5 left-0.5 w-[16px] h-[16px] bg-white dark:bg-gray-900 rounded-full shadow
                    transform transition-transform duration-200
                    ${checked ? 'translate-x-[17px]' : 'translate-x-0'}
                `} />
            </div>
            <span className={`text-[9px] font-medium ${checked ? 'text-gray-800 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                {checked ? 'Active' : 'Inactive'}
            </span>
        </label>
    );
    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";
    return (
        <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${isDarkMode ? 'dark bg-[#0B1120]' : 'bg-gray-50'}`}>
            <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

            <NavBar />
            <div className="flex flex-1 overflow-hidden">
                        <div
            className={`
              ${isToggleSidebar ? "w-64" : "w-[72px]"}
              m-3
              rounded-3xl
              ${glass}
              overflow-hidden
              transition-all
              duration-300
              ease-in-out
            `}
          >
            <Sidebar />
          </div>
                <div className={`flex-1 p-4 md:p-5 w-full overflow-hidden flex flex-col transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-gray-200'}`}>
                    <div className="max-w-5xl mx-auto w-full flex flex-col h-full">

                        {/* Header */}
                        <div className="flex-shrink-0 flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg border transition-colors duration-300 ${isDarkMode ? 'bg-[#111827] border-gray-700' : 'bg-white border-gray-200'}`}>
                                    <MessageSquare className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h1 className={`text-sm font-bold tracking-tight transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>WhatsApp Rates</h1>
                                    <p className={`text-[9px] transition-colors duration-300 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>Manage your messaging rates</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowForm(true)}
                                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[10px] font-medium rounded-lg transition-colors ${isDarkMode ? 'bg-white text-gray-800 hover:bg-gray-200' : 'bg-blue-800 text-white hover:opacity-80'}`}
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add Rate
                            </button>
                        </div>

                        {/* Table */}
                        <div className={`flex-1 border rounded-xl shadow-sm overflow-hidden flex flex-col min-h-0 transition-colors duration-300 ${isDarkMode ? 'bg-[#111827] border-gray-800' : 'bg-white border-gray-200'}`}>
                            <div className={`shrink-0 px-5 py-2.5 border-b transition-colors duration-300 ${isDarkMode ? 'bg-[#1F2937] border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
                                <h2 className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Current Rates</h2>
                            </div>

                            {loading && rates.length === 0 ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className={`animate-spin rounded-full h-8 w-8 border-2 border-t-transparent mx-auto ${isDarkMode ? 'border-gray-500' : 'border-gray-400'}`}></div>
                                        <p className={`mt-2 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading rates...</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 overflow-y-auto custom-scrollbar">
                                    <table className="min-w-full">
                                        <thead className={`sticky top-0 z-10 transition-colors duration-300 ${isDarkMode ? 'bg-[#1F2937]' : 'bg-blue-700'}`}>
                                            <tr>
                                                <th className={`px-4 py-2 text-left text-[9px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-white'}`}>Message Type</th>
                                                <th className={`px-4 py-2 text-left text-[9px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-white'}`}>Rate</th>
                                                <th className={`px-4 py-2 text-left text-[9px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-white'}`}>Description</th>
                                                <th className={`px-4 py-2 text-left text-[9px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-white'}`}>Status</th>
                                                <th className={`px-4 py-2 text-left text-[9px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-white'}`}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y transition-colors duration-300 ${isDarkMode ? 'divide-gray-800' : 'divide-gray-100'}`}>
                                            {rates.map((rate, index) => (
                                                <tr key={rate.id || index} className={`transition-colors ${isDarkMode ? 'hover:bg-[#1F2937]/80' : 'hover:bg-gray-50'}`}>
                                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`p-1.5 rounded-md transition-colors duration-300 ${isDarkMode ? 'bg-[#1F2937]' : 'bg-white'}`}>
                                                                <MessageSquare className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                                                            </div>
                                                            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                                                {rate.message_type}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                                        <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                                            ₹{rate.rate}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <div className={`text-[10px] max-w-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                            {rate.description}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                                        <StatusSwitch
                                                            checked={rate.status === 'Active'}
                                                            onChange={(e) => {
                                                                const newStatus = e.target.checked ? 'Active' : 'Inactive';
                                                                handleStatusToggle(rate, newStatus);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                                        <button
                                                            onClick={() => handleEdit(rate)}
                                                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[9px] rounded-lg transition-colors font-medium ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                        >
                                                            <Edit className="w-3 h-3 text-blue-600 dark:text-sky-400" />
                                                            Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {rates.length === 0 && !loading && (
                                                <tr>
                                                    <td colSpan="5" className="px-4 py-10 text-center">
                                                        <MessageSquare className={`w-6 h-6 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                                                        <p className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No rates found</p>
                                                        <p className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Add your first rate to get started</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 mt-3 text-center">
                            <p className={`text-[8px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {rates.length} rate{rates.length !== 1 ? 's' : ''} configured
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <RateFormModal />
            {showAlert && (
                <CustomAlert
                    config={alertConfig}
                    onClose={() => setShowAlert(false)}
                />
            )}
        </div>
    );
};

export default WhatsAppRatesManager;